/**
 * Tabs — the one tab strip. Four hand-rolled implementations predated this
 * (ProjectDashboard ×2, EvaluationPanel, and the since-removed
 * CompliancePanel); both of their visual dialects survive as variants:
 *
 *  - `underline`: page-level section switching (border-b, brand underline)
 *  - `segmented`: local view/filter toggles (pill group on surface-secondary)
 *
 * Proper tablist semantics with roving tabindex — Left/Right/Home/End move
 * selection, which none of the hand-rolled strips did. Adoption normalises
 * every strip to this one scale; per-strip font sizes did not survive on
 * purpose. Pass `idBase` and spread `tabPanelProps(idBase, activeId)` onto
 * the panel container to complete the tab ↔ panel ARIA wiring.
 */
import { useRef } from "react";
import type { ComponentType, ReactNode } from "react";

export interface TabItem<T extends string = string> {
  id: T;
  label: string;
  /** Lucide icon component (underline variant renders it at 14px, segmented at 13px). */
  icon?: ComponentType<{ size?: number | string }>;
  /** Count or ratio shown after the label, e.g. 12 or "34/56". */
  badge?: ReactNode;
}

export type TabsVariant = "underline" | "segmented";

/**
 * `ai` marks a machine-authored surface (purple, per the colour canon) —
 * EvaluationPanel's strip must read as AI, not as brand navigation. Both
 * variants honour it; a silent no-op would strip the canon marker.
 */
export type TabsAccent = "brand" | "ai";

/** Pure so bun:test can cover the class matrix without a DOM. */
export function tabClasses(variant: TabsVariant, active: boolean, accent: TabsAccent = "brand"): string {
  if (variant === "segmented") {
    return [
      "flex items-center gap-1 px-2.5 py-1 rounded-md text-[13px] font-medium transition-colors whitespace-nowrap",
      active
        ? `bg-surface shadow-sm ${accent === "ai" ? "text-ai" : "text-text-primary"}`
        : "text-text-secondary hover:text-text-primary",
    ].join(" ");
  }
  return [
    "flex items-center gap-1.5 px-3 sm:px-4 py-2.5 text-[14px] sm:text-[15px] font-medium border-b-2 transition-colors whitespace-nowrap",
    active
      ? accent === "ai" ? "border-ai text-ai" : "border-brand text-brand"
      : "border-transparent text-text-secondary hover:text-text-primary",
  ].join(" ");
}

export function tabListClasses(variant: TabsVariant, extra?: string): string {
  const base =
    variant === "segmented"
      ? "flex items-center gap-0.5 bg-surface-secondary rounded-lg p-0.5 w-fit"
      : "flex items-center gap-0 border-b border-border overflow-x-auto";
  return extra ? `${base} ${extra}` : base;
}

/**
 * Next selection for arrow/Home/End keys. Pure and total: an empty list
 * returns null; a stale/unknown active id resets to an end of the list
 * instead of indexing off it.
 */
export function nextTabId<T extends string>(
  items: ReadonlyArray<TabItem<T>>,
  activeId: string,
  key: "ArrowLeft" | "ArrowRight" | "Home" | "End",
): T | null {
  if (!items.length) return null;
  if (key === "Home") return items[0].id;
  if (key === "End") return items[items.length - 1].id;
  const idx = items.findIndex((t) => t.id === activeId);
  if (idx === -1) return key === "ArrowRight" ? items[0].id : items[items.length - 1].id;
  const dir = key === "ArrowRight" ? 1 : items.length - 1;
  return items[(idx + dir) % items.length].id;
}

/** Spread onto the panel a strip with `idBase` controls. */
export function tabPanelProps(idBase: string, activeId: string) {
  return {
    role: "tabpanel" as const,
    id: `${idBase}-panel-${activeId}`,
    "aria-labelledby": `${idBase}-tab-${activeId}`,
  };
}

export interface TabsProps<T extends string> {
  items: TabItem<T>[];
  active: T;
  onChange: (id: T) => void;
  variant?: TabsVariant;
  accent?: TabsAccent;
  /** What this strip switches between, for assistive tech. */
  label: string;
  /** Stable prefix wiring tab ids to a tabPanelProps() panel. */
  idBase?: string;
  className?: string;
}

export function Tabs<T extends string>({
  items,
  active,
  onChange,
  variant = "underline",
  accent = "brand",
  label,
  idBase,
  className,
}: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const activeIdx = items.findIndex((t) => t.id === active);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    const next = nextTabId(items, active, e.key);
    if (next == null) return;
    onChange(next);
    listRef.current
      ?.querySelector<HTMLButtonElement>(`[data-tab-id="${CSS.escape(next)}"]`)
      ?.focus();
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className={tabListClasses(variant, className)}
    >
      {items.map((tab, i) => {
        const isActive = tab.id === active;
        return (
          <button
            key={tab.id}
            data-tab-id={tab.id}
            id={idBase ? `${idBase}-tab-${tab.id}` : undefined}
            role="tab"
            aria-selected={isActive}
            aria-controls={idBase ? `${idBase}-panel-${tab.id}` : undefined}
            // Roving tabindex; if `active` matches nothing (e.g. a stale
            // ?tab= deep link) the first tab stays reachable — otherwise the
            // whole strip would drop out of the keyboard order.
            tabIndex={isActive || (activeIdx === -1 && i === 0) ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={tabClasses(variant, isActive, accent)}
          >
            {tab.icon && <tab.icon size={variant === "segmented" ? 13 : 14} />}
            {tab.label}
            {tab.badge != null && (
              <span className="ml-1 text-[12px] text-text-secondary">{tab.badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
